import {
  BookStudyUnitName,
  CreatingResilientAusClassroomsId,
  CreatingResilientClassroomsId,
  lessonTypeOptionsWithNone,
} from "@constants";
import { MIXPANEL_LESSON_TYPE } from "@src/constants/analyticsTracking";
import { SllLesson } from "@api/graphql-codegen";

type StandaloneLessonTypeArgs = {
  lesson: Pick<SllLesson, "uniqueName" | "lessonType">;
  programName?: string | undefined;
};

type UnitLessonTypeArgs = {
  unitName?: string | undefined | null;
  unitId?: string | undefined | null;
  unitUniqueName?: string | undefined | null;
};

/** Legacy / alternate labels → canonical Mixpanel lessonType */
const LESSON_TYPE_ALIASES: Record<string, string> = {
  StudentIntroduction: MIXPANEL_LESSON_TYPE.STUDENT_INTRODUCTION,
  "Student Introduction": MIXPANEL_LESSON_TYPE.STUDENT_INTRODUCTION,
};

const STANDALONE_LESSON_TYPE_BY_LABEL: Record<string, string> = {
  None: MIXPANEL_LESSON_TYPE.NONE,
  "Student Voice": MIXPANEL_LESSON_TYPE.STUDENT_VOICE,
  "Positive Pathways": MIXPANEL_LESSON_TYPE.POSITIVE_PATHWAYS,
  "Brain Break": MIXPANEL_LESSON_TYPE.BRAIN_BREAK,
  "Skill-Builder": MIXPANEL_LESSON_TYPE.SKILL_BUILDER,
  "Educator Content": MIXPANEL_LESSON_TYPE.EDUCATOR_CONTENT,
  Resource: MIXPANEL_LESSON_TYPE.RESOURCE,
  Caregiver: MIXPANEL_LESSON_TYPE.CAREGIVER,
  "Student Introduction": MIXPANEL_LESSON_TYPE.STUDENT_INTRODUCTION,
  StudentIntroduction: MIXPANEL_LESSON_TYPE.STUDENT_INTRODUCTION,
};

/** Map raw SuperAdmin lessonType codes → Mixpanel labels (Edit Lesson options) */
const STANDALONE_LESSON_TYPE_BY_VALUE: Record<string, string> = {
  "9": MIXPANEL_LESSON_TYPE.NONE,
  "0": MIXPANEL_LESSON_TYPE.STUDENT_VOICE,
  "1": MIXPANEL_LESSON_TYPE.POSITIVE_PATHWAYS,
  "2": MIXPANEL_LESSON_TYPE.BRAIN_BREAK,
  "3": MIXPANEL_LESSON_TYPE.SKILL_BUILDER,
  "4": MIXPANEL_LESSON_TYPE.EDUCATOR_CONTENT,
  "5": MIXPANEL_LESSON_TYPE.RESOURCE,
  "6": MIXPANEL_LESSON_TYPE.STUDENT_INTRODUCTION,
  "7": MIXPANEL_LESSON_TYPE.CAREGIVER,
};

const normalizeMixpanelLessonType = (value?: string | null): string => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return LESSON_TYPE_ALIASES[trimmed] || trimmed;
};

/**
 * Resolves Mixpanel `lessonType` for standalone lessons.
 * Always returns a SuperAdmin Lesson Type label (never empty / not set).
 * Consolidates "StudentIntroduction" → "Student Introduction".
 */
export function resolveStandaloneMixpanelLessonType({
  lesson,
}: StandaloneLessonTypeArgs): string {
  const uniqueName = (lesson.uniqueName || "").toLowerCase();
  const isPrePostProgramQuiz =
    uniqueName.includes("preprogram-quiz") ||
    uniqueName.includes("postprogram-quiz");
  if (isPrePostProgramQuiz) return MIXPANEL_LESSON_TYPE.QUIZ;

  const raw = (lesson.lessonType ?? "").trim();
  const normalized = normalizeMixpanelLessonType(raw);

  if (
    normalized &&
    (Object.values(MIXPANEL_LESSON_TYPE) as string[]).includes(normalized)
  ) {
    return normalized;
  }

  if (raw && STANDALONE_LESSON_TYPE_BY_VALUE[raw]) {
    return STANDALONE_LESSON_TYPE_BY_VALUE[raw];
  }

  const label = lessonTypeOptionsWithNone.find(
    (opt) => opt.value === lesson.lessonType,
  )?.label;

  if (label && STANDALONE_LESSON_TYPE_BY_LABEL[label]) {
    return STANDALONE_LESSON_TYPE_BY_LABEL[label];
  }

  if (label && STANDALONE_LESSON_TYPE_BY_LABEL[label.replace(/\s/g, "")]) {
    return STANDALONE_LESSON_TYPE_BY_LABEL[label.replace(/\s/g, "")];
  }

  return MIXPANEL_LESSON_TYPE.NONE;
}

/**
 * Resolves Mixpanel `lessonType` for unit/course lessons.
 * Always returns a non-empty label. Standard curriculum units use "Unit"
 * (they have no SuperAdmin lessonType — avoid empty / not set).
 */
export function resolveUnitMixpanelLessonType({
  unitName,
  unitId,
  unitUniqueName,
}: UnitLessonTypeArgs): string {
  if (unitName === "Pre-Program Quiz" || unitName === "Post-Program Quiz") {
    return MIXPANEL_LESSON_TYPE.QUIZ;
  }

  if (unitName === "Educator Onboarding") {
    return MIXPANEL_LESSON_TYPE.ONBOARDING;
  }

  if (unitName === "Educator Wellbeing") {
    return MIXPANEL_LESSON_TYPE.EDUCATOR_CONTENT;
  }

  if (unitUniqueName === BookStudyUnitName) {
    return MIXPANEL_LESSON_TYPE.EDUCATOR_CONTENT;
  }

  if (
    unitId === CreatingResilientClassroomsId ||
    unitId === CreatingResilientAusClassroomsId
  ) {
    return MIXPANEL_LESSON_TYPE.EDUCATOR_CONTENT;
  }

  return MIXPANEL_LESSON_TYPE.UNIT;
}
